import { Link } from "react-router-dom";
import type { ComponentHistory } from "../../lib/api";
import { HistoryTimeline } from "../../pages/MaintenanceHistory";
import { assetPath } from "../register/exports";
import { SourceLink } from "../SourceLink";
import { Empty } from "../States";
import { fieldHeading, type RunView } from "./moduleFlow";
import { lifecycleLabel, percent } from "../../lib/words";
import { TableScroll } from "../TableScroll";

type Json = Record<string, unknown>;
const words = (s: unknown) => String(s ?? "").replace(/_/g, " ");
const list = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

const OUTCOMES = [
  { key: "cleared", label: "Cleared", tile: "good" },
  { key: "needs_review", label: "Needs review", tile: "highlight" },
  { key: "no_document", label: "No document", tile: "" },
  { key: "not_attempted", label: "Not attempted", tile: "" },
] as const;

function PackageResult({ run, f }: { run: RunView; f: Json }) {
  const outcomes = (f.outcomes ?? {}) as Record<string, string>;
  const tags = Object.keys(outcomes);
  if (tags.length === 0) {
    return run.status === "not_found"
      ? <Empty title={`The survey found no tags in ${run.package ?? "this package"}`}>Neither the master index nor the content sweep names a tag.</Empty>
      : <Empty title="No tags finished yet" />;
  }
  const count = (k: string) => tags.filter((t) => outcomes[t] === k).length;
  return (
    <div className="stack">
      <div className="tiles">
        {OUTCOMES.map((o) => (
          <div key={o.key} className={`card tile ${o.tile}`}>
            <div className="n">{count(o.key)}</div>
            <div className="l">{o.label}</div>
          </div>
        ))}
      </div>
      <TableScroll label="Tags by outcome">
      <table>
        <thead><tr><th>Tag</th><th>Outcome</th></tr></thead>
        <tbody>
          {tags.map((t) => (
            <tr key={t}>
              <td className="mono">
                {outcomes[t] === "cleared" && run.package
                  ? <Link to={assetPath(run.package, t)}>{t}</Link>
                  : t}
              </td>
              <td>{OUTCOMES.find((o) => o.key === outcomes[t])?.label ?? words(outcomes[t])}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </TableScroll>
    </div>
  );
}

function IdentityResult({ f }: { f: Json }) {
  const fields = list<Json>(f.fields);
  if (fields.length === 0) return <Empty title="No fields were read" />;
  return (
    <TableScroll label="Fields read">
    <table>
      <thead><tr><th>Field</th><th>Value</th><th style={{ textAlign: "right" }}>Confidence</th></tr></thead>
      <tbody>
        {fields.map((x) => (
          <tr key={String(x.name)}>
            <td>{fieldHeading(String(x.name ?? ""))}</td>
            <td className="mono">{x.value == null || x.value === "" ? "not found" : String(x.value)}</td>
            <td className="mono" style={{ textAlign: "right" }}>{percent(typeof x.confidence === "number" ? x.confidence : null)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </TableScroll>
  );
}

function ComparisonResult({ f }: { f: Json }) {
  const basis = (f.basis ?? {}) as Json;
  return (
    <div className="stack">
      <p className="muted"><span className="mono">{String(f.old_key ?? "")}</span> against <span className="mono">{String(f.new_key ?? "")}</span></p>
      {typeof basis.statement === "string" && <p style={{ fontSize: 14 }}>{basis.statement}</p>}
      <TableScroll label="Specification differences">
      <table>
        <thead><tr><th>Field</th><th>Installed</th><th>Replacement</th><th>Result</th><th>Matters</th></tr></thead>
        <tbody>
          {list<Json>(f.fields).map((x) => (
            <tr key={String(x.name)}>
              <td>{fieldHeading(String(x.name ?? ""))}</td>
              <td className="mono">{x.old == null ? "" : String(x.old)}</td>
              <td className="mono">{x.new == null ? "" : String(x.new)}</td>
              <td><span className={`pill ${x.status === "same" ? "ok" : "review"}`}>{words(x.status)}</span></td>
              <td>{x.significance ? words(x.significance) : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </TableScroll>
    </div>
  );
}

function ApplicabilityResult({ f }: { f: Json }) {
  const unresolved = list<string>(f.unresolved);
  const matched = list<Json>(f.matched);
  return (
    <div className="stack">
      <p><span className={`pill ${f.status === "applicable" ? "review" : f.status === "not_applicable" ? "ok" : "info"}`}>{words(f.status)}</span></p>
      {typeof f.reason === "string" && f.reason && <p style={{ fontSize: 14 }}>{f.reason}</p>}
      {matched.length > 0 && (
        <p style={{ fontSize: 14 }}>Decided by: {matched.map((m) => `${String(m.tag ?? "")} ${String(m.value ?? "")}`.trim()).join(", ")}</p>
      )}
      {unresolved.length > 0 && (
        <div>
          <p className="muted" style={{ fontSize: 14 }}>The register says nothing about:</p>
          <ul style={{ paddingLeft: 20, fontSize: 14 }}>{unresolved.map((u) => <li key={u}>{u}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function StandardizationResult({ f }: { f: Json }) {
  const template = (f.template ?? {}) as Json;
  const missing = list<string>(f.mandatory_not_found);
  return (
    <div className="stack">
      <p><span className="pill info">{words(f.status)}</span> {typeof template.name === "string" && template.name}</p>
      {f.status === "no_template_for_class" && (
        <p style={{ fontSize: 14 }}>No data sheet is loaded for {String(f.equipment_class || "this class")}, so nothing was mapped.</p>
      )}
      {missing.length > 0 && <p style={{ fontSize: 14 }}>Mandatory fields not found: {missing.join(", ")}</p>}
      {list<Json>(f.fields).length > 0 && (
        <TableScroll label="Data sheet fields">
        <table>
          <thead><tr><th>Data sheet field</th><th>Value</th><th>Result</th></tr></thead>
          <tbody>
            {list<Json>(f.fields).map((x) => (
              <tr key={String(x.attribute)}>
                <td>{String(x.attribute)}{x.mandatory ? " (mandatory)" : ""}</td>
                <td className="mono">{x.value == null ? "" : String(x.value)}</td>
                <td><span className={`pill ${x.status === "filled" ? "ok" : "review"}`}>{words(x.status)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      )}
    </div>
  );
}

function LifecycleResult({ f, pkg }: { f: Json; pkg: string | null }) {
  const evidence = (f.evidence ?? null) as Json | null;
  const tone = f.status === "active" ? "ok" : f.status === "no_notice_found" ? "info" : "review";
  return (
    <div className="stack">
      {f.status != null && <p><span className={`pill ${tone}`}>{lifecycleLabel(String(f.status))}</span></p>}
      {f.disagreement === true && <p style={{ fontSize: 14 }}>Sources disagree, so a person needs to check this finding.</p>}
      {evidence && (
        <div className="stack" style={{ gap: 6 }}>
          {typeof evidence.quote === "string" && evidence.quote && <blockquote style={{ fontSize: 14 }}>{evidence.quote}</blockquote>}
          {typeof evidence.source_url === "string" && <SourceLink url={evidence.source_url} pkg={pkg} />}
          {typeof evidence.successor_part === "string" && evidence.successor_part && (
            <p style={{ fontSize: 14 }}>Successor named: <span className="mono">{evidence.successor_part}</span></p>
          )}
        </div>
      )}
      {typeof f.sources_read === "number" && <p className="muted" style={{ fontSize: 13 }}>{f.sources_read} sources read.</p>}
    </div>
  );
}

function body(run: RunView, f: Json) {
  const t = run.request_type.toLowerCase();
  if (t.startsWith("package")) return <PackageResult run={run} f={f} />;
  if (t === "asset.identity" || t === "identity" || t === "uc1.identity_lookup") return <IdentityResult f={f} />;
  if (t === "spec.comparison") return <ComparisonResult f={f} />;
  if (t === "spec.standardization") return <StandardizationResult f={f} />;
  if (t === "site.applicability") return <ApplicabilityResult f={f} />;
  if (t === "maintenance.insights") return <HistoryTimeline history={f as unknown as ComponentHistory} pkg={run.package ?? null} />;
  return <LifecycleResult f={f} pkg={run.package ?? null} />;
}

/** What a run produced, in the shape its module reports, with the raw result one click away. */
export function RunFindings({ run }: { run: RunView }) {
  const f = run.findings;
  const usd = typeof run.spend?.usd === "number" ? run.spend.usd : null;
  if (!f || Object.keys(f).length === 0) {
    return <Empty title="No result yet">The result appears here when the run finishes or stops at a gate.</Empty>;
  }
  return (
    <section className="card stack" aria-label="Result">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h3 style={{ fontSize: 18 }}>Result</h3>
        {usd !== null && <span className="muted mono">Spent ${usd.toFixed(2)}</span>}
      </div>
      {body(run, f)}
      <details>
        <summary className="muted" style={{ cursor: "pointer", fontSize: 13 }}>Raw result</summary>
        <pre className="mono" style={{ fontSize: 12, overflowX: "auto", whiteSpace: "pre-wrap" }}>{JSON.stringify(f, null, 2)}</pre>
      </details>
    </section>
  );
}
